import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { loadLessons } from '../../../Redux/lessonsSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import type { EnsembleModel, MemberChargeModel } from '../../../Models/EnsembleModel';
import { EnsembleService } from '../../../Services/EnsembleService';
import { displayNameOf, ensembleDisplayName } from '../../../Utils/DisplayNameUtil';
import { localDateString } from '../../../Utils/LessonStatusUtil';
import { Modal } from '../../SharedArea/Modal/Modal';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { useToast } from '../../SharedArea/Toast/ToastProvider';
import './Ensembles.css';

interface ScheduleMeetingProps {
  ensemble: EnsembleModel;
  onClose: () => void;
}

/**
 * Booking one meeting.
 *
 * The charges are read from the server rather than worked out here, and they
 * are shown before anything is written. That is the whole point of the screen:
 * a tutor confirms the exact figures that will land in the ledger, down to
 * which member gets the remainder shekel of an uneven split, instead of
 * finding out afterwards.
 *
 * Under `institution` the list is one line, the organization, and everybody
 * taught is named as unbilled. Saying so plainly is better than an empty list
 * that looks like a bug.
 */
export function ScheduleMeeting({ ensemble, onClose }: ScheduleMeetingProps) {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const students = useAppSelector(state => state.students.items);

  const [date, setDate] = useState(localDateString());
  const [time, setTime] = useState(ensemble.slots[0]?.time ?? '');
  const [charges, setCharges] = useState<MemberChargeModel[] | null>(null);
  const [chargeError, setChargeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    EnsembleService.charges(ensemble.name)
      .then(result => { if (live) { setCharges(result); setChargeError(null); } })
      .catch((failure: unknown) => {
        if (!live) return;
        setCharges([]);
        // A group with no valid payer cannot be booked at all, and the reason
        // belongs on screen rather than only in a failed save afterwards.
        setChargeError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
      });
    return () => { live = false; };
  }, [ensemble.name]);

  const billed = new Set((charges ?? []).map(charge => charge.student));
  const unbilled = ensemble.members.filter(member => !billed.has(member));
  const bookable = charges !== null && charges.length > 0 && Boolean(date);

  async function book() {
    setBusy(true);
    setError(null);
    try {
      const meeting = await EnsembleService.schedule(ensemble.name, {
        date,
        time: time || null
      });
      // The lessons it wrote are ordinary lessons, so the ledger the rest of
      // the app reads has to be refreshed rather than patched by hand here.
      void dispatch(loadLessons());
      toast({ message: tr('meetingBooked', meeting.lessonIds.length) });
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tr('scheduleMeeting')}
      subtitle={ensembleDisplayName(ensemble, language)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="button" className="primary-button" onClick={() => void book()} disabled={busy || !bookable}>
            {busy ? <Spinner inline /> : tr('save')}
          </button>
        </>
      }
    >
      <div className="meeting-form">
        <label className="field">
          <span>{tr('date')}</span>
          <input className="text-input" type="date" value={date} onChange={e => setDate(e.target.value)} required />
        </label>
        <label className="field">
          <span>{tr('time')}</span>
          <input className="text-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
        </label>
      </div>

      <section className="meeting-charges">
        <h3>{tr('meetingCharges')}</h3>
        {charges === null && <Spinner inline />}
        {chargeError && <p className="editor-section-error" role="alert">{tr('meetingNobody')}</p>}
        {charges !== null && charges.length > 0 && (
          <ul className="meeting-charge-list">
            {charges.map(charge => (
              <li key={charge.student}>
                <span>{displayNameOf(charge.student, students, language)}</span>
                <strong><MoneyText amount={charge.amount} /></strong>
              </li>
            ))}
          </ul>
        )}
        {unbilled.length > 0 && (
          <p className="dashboard-quiet">
            {tr('meetingUnbilled', unbilled.length)}
            {': '}
            <bdi>{unbilled.map(member => displayNameOf(member, students, language)).join(', ')}</bdi>
          </p>
        )}
        {error && <p className="editor-section-error" role="alert">{tr('errorGeneric')}</p>}
      </section>
    </Modal>
  );
}
